/**
 * EStepsCalibration Component
 * PE320 Extruder G-Code calibration tool
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './EStepsCalibration.css';
import { RelatedGuide } from '../../components/ui';
import { chartColours, useTheme } from '../../utils/theme';

// PE320 Specifications
const MAX_SCREW_SPEED = 159.6;
const MAX_FEED_RATE = 8818.21;

const EStepsCalibration = () => {
  const [materialName, setMaterialName] = useState('');
  const [nozzleSize, setNozzleSize] = useState('');
  const [gcodeSpeed, setGcodeSpeed] = useState('');
  const [gcodeDuration, setGcodeDuration] = useState('60');
  const [generatedGcode, setGeneratedGcode] = useState('');
  const [rows, setRows] = useState([
    { id: 1, gmin: '', rpm: '' },
    { id: 2, gmin: '', rpm: '' },
    { id: 3, gmin: '', rpm: '' }
  ]);
  const [coefficients, setCoefficients] = useState(null);
  const [fit, setFit] = useState(null);
  const [libsReady, setLibsReady] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', error: false });

  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const nextRowId = useRef(4);

  const loadExternalScript = (src) => new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });

  useEffect(() => {
    let cancelled = false;
    const MATH_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/mathjs/11.11.0/math.min.js';
    const CHART_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.0/chart.umd.min.js';

    const ensureLibs = async () => {
      const jobs = [];
      if (!window.math) jobs.push(loadExternalScript(MATH_SRC));
      if (!window.Chart) jobs.push(loadExternalScript(CHART_SRC));
      try {
        await Promise.all(jobs);
      } catch (error) {
        console.error('Failed to load calibration libraries', error);
        return;
      }
      if (!cancelled && window.math && window.Chart) {
        setLibsReady(true);
      }
    };

    if (window.math && window.Chart) {
      setLibsReady(true);
    } else {
      ensureLibs();
    }

    return () => {
      cancelled = true;
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []);

  const notify = (message, error = false) => {
    setNotification({ show: true, message, error });
    setTimeout(() => setNotification({ show: false, message: '', error: false }), 3000);
  };

  const calculateKgHr = (gmin) => {
    const g = parseFloat(gmin);
    return isNaN(g) ? '—' : ((g * 60) / 1000).toFixed(4);
  };

  const handleRowChange = (id, field, value) => {
    setRows(rows.map(row => 
      row.id === id ? { ...row, [field]: value } : row
    ));
  };

  const addRow = (gmin = '', rpm = '') => {
    setRows([...rows, { id: nextRowId.current++, gmin, rpm }]);
  };

  const removeRow = (id) => {
    if (rows.length > 1) {
      setRows(rows.filter(row => row.id !== id));
    }
  };

  const generateGcode = () => {
    const speed = parseFloat(gcodeSpeed);
    const duration = parseFloat(gcodeDuration);

    if (isNaN(speed) || speed < 0 || speed > MAX_SCREW_SPEED) {
      notify(`Please enter a valid mixing screw speed (0-${MAX_SCREW_SPEED} RPM)`, true);
      return;
    }

    if (isNaN(duration) || duration <= 0) {
      notify('Please enter a valid duration (seconds)', true);
      return;
    }

    const ratio = speed / MAX_SCREW_SPEED;
    const eValue = (ratio * MAX_FEED_RATE * duration / 60).toFixed(2);
    const fValue = (ratio * MAX_FEED_RATE).toFixed(2);

    setGeneratedGcode(`G1 E${eValue} F${fValue}`);
  };

  const copyGcode = () => {
    if (!generatedGcode) return;
    navigator.clipboard.writeText(generatedGcode).then(() => {
      notify('G-code copied to clipboard');
    }).catch(() => {
      notify('Failed to copy G-code', true);
    });
  };

  const autoFillSpeeds = () => {
    const percentages = [0.2, 0.4, 0.6, 0.8, 1.0];
    const newRows = percentages.map((pct, index) => ({
      id: nextRowId.current++,
      gmin: '',
      rpm: (MAX_SCREW_SPEED * pct).toFixed(1)
    }));
    setRows(newRows);
    notify('Test speeds auto-filled (20%, 40%, 60%, 80%, 100% of max)');
  };

  const drawChart = useCallback((xs, ys, beta) => {
    if (!window.Chart || !chartRef.current) return;

    const min = Math.min(...xs);
    const max = Math.max(...xs);
    const pts = 100;
    const step = (max - min) / pts;
    const curveX = [];
    const curveY = [];

    for (let i = 0; i <= pts; i++) {
      const x = min + i * step;
      curveX.push(x);
      curveY.push(beta[0] * Math.pow(x, 3) + beta[1] * Math.pow(x, 2) + beta[2] * x + beta[3]);
    }

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const chart = chartColours();
    chartInstance.current = new window.Chart(chartRef.current, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: 'Measured',
            data: xs.map((x, i) => ({ x: xs[i], y: ys[i] })),
            pointRadius: 6,
            backgroundColor: '#6e8196' // steel (Graphite & Steel)
          },
          {
            label: 'Fit',
            data: curveX.map((x, i) => ({ x, y: curveY[i] })),
            type: 'line',
            borderWidth: 2,
            pointRadius: 0,
            borderColor: '#4CAF50'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            title: { display: true, text: 'Throughput (kg/hr)', color: chart.ink },
            ticks: { color: chart.ink },
            grid: { color: chart.grid }
          },
          y: {
            title: { display: true, text: 'Mixing Screw Speed (rpm)', color: chart.ink },
            ticks: { color: chart.ink },
            grid: { color: chart.grid }
          }
        },
        plugins: {
          legend: { labels: { color: chart.ink } }
        }
      }
    });
  }, []);

  // Redraw the canvas chart in the new colours when the theme changes
  const theme = useTheme();
  useEffect(() => {
    if (!fit || !libsReady || !coefficients) return;
    drawChart(fit.xs, fit.ys, fit.beta);
  }, [fit, libsReady, coefficients, drawChart, theme]);

  const calculateCoefficients = () => {
    if (!window.math) {
      notify('Math library not loaded. Please refresh the page.', true);
      return;
    }

    const xs = [];
    const ys = [];

    rows.forEach(row => {
      const g = parseFloat(row.gmin);
      const rpm = parseFloat(row.rpm);
      if (!isNaN(g) && !isNaN(rpm)) {
        xs.push((g * 60) / 1000);
        ys.push(rpm);
      }
    });

    if (xs.length < 4) {
      notify('Need at least 4 valid rows', true);
      return;
    }

    const X = xs.map(x => [Math.pow(x, 3), Math.pow(x, 2), x, 1]);
    const beta = window.math.multiply(
      window.math.inv(window.math.multiply(window.math.transpose(X), X)),
      window.math.multiply(window.math.transpose(X), ys)
    );

    setCoefficients({
      coef3: beta[0].toPrecision(10),
      coef2: beta[1].toPrecision(10),
      coef1: beta[2].toPrecision(10),
      coef0: beta[3].toPrecision(10)
    });
    setFit({ xs, ys, beta });
    notify('Coefficients calculated');
  };

  const exportCsv = () => {
    if (!coefficients) {
      notify('Please calculate coefficients first', true);
      return;
    }

    let csv = `PE320 Extruder Calibration\n`;
    csv += `Material: ${materialName || 'Unknown'}\nNozzle Size: ${nozzleSize || 'Unknown'} mm\n`;
    csv += `Max Mixing Screw Speed: ${MAX_SCREW_SPEED} RPM\n\n`;
    csv += 'g/min,kg/hr,Mixing Screw Speed (rpm)\n';
    
    rows.forEach(row => {
      if (row.gmin && row.rpm) {
        csv += `${row.gmin},${calculateKgHr(row.gmin)},${row.rpm}\n`;
      }
    });
    
    csv += '\nCoefficients (Throughput = aX3 + bX2 + cX + d)\nX3,X2,X1,Y\n';
    csv += `${coefficients.coef3},${coefficients.coef2},${coefficients.coef1},${coefficients.coef0}`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `PE320_calibration_${materialName || 'unknown'}_${nozzleSize || 'unknown'}mm.csv`;
    link.click();
    notify('CSV exported');
  };

  const saveData = () => {
    const data = {
      materialName,
      nozzleSize,
      date: new Date().toLocaleDateString(),
      rows: rows.filter(r => r.gmin || r.rpm).map(r => ({ g: r.gmin, rpm: r.rpm }))
    };

    let calibrations = [];
    const savedData = localStorage.getItem('pe320Calibrations');
    if (savedData) {
      try {
        calibrations = JSON.parse(savedData);
      } catch (error) {
        console.error('Error parsing saved data:', error);
      }
    }

    calibrations.push(data);
    localStorage.setItem('pe320Calibrations', JSON.stringify(calibrations));
    notify('Data saved successfully');
  };

  const loadData = () => {
    const savedData = localStorage.getItem('pe320Calibrations');
    if (!savedData) {
      notify('No saved calibrations found', true);
      return;
    }

    try {
      const calibrations = JSON.parse(savedData);
      if (calibrations.length === 0) {
        notify('No saved calibrations found', true);
        return;
      }

      const latest = calibrations[calibrations.length - 1];
      setMaterialName(latest.materialName || '');
      setNozzleSize(latest.nozzleSize || '');
      
      if (latest.rows && latest.rows.length > 0) {
        setRows(latest.rows.map((row) => ({
          id: nextRowId.current++,
          gmin: row.g || '',
          rpm: row.rpm || ''
        })));
      }
      
      notify('Calibration loaded successfully');
    } catch (error) {
      notify('Error loading calibration', true);
      console.error('Error loading data:', error);
    }
  };

  return (
    <div className="esteps-tool">
      {/* Notification */}
      <div className={`notification ${notification.show ? 'show' : ''} ${notification.error ? 'error' : ''}`}>
        {notification.message}
      </div>

      <div className="esteps-container">
        {/* Header Panel */}
        <section className="panel">
          <div className="panel-header">
            <h1>PE320 Extruder Calibration Tool</h1>
          </div>
          <RelatedGuide
            title="Related guide"
            courseHref="/field-ops-course.html?id=pe320-operator"
            manualHref="/field-ops-manual.html?set=apollo&course=pe320-operator"
          >
            E-steps sits inside PE320 operator training. Use the PE320 operator course and the Apollo cell manual for the full procedure.
          </RelatedGuide>

          <div className="info-box">
            <h3>PE320 Specifications</h3>
            <table className="spec-table">
              <tbody>
                <tr><td>Motor Max Speed:</td><td>3990 RPM</td></tr>
                <tr><td>Gearbox Reduction:</td><td>25:1</td></tr>
                <tr><td>Mixing Screw Max Speed:</td><td>159.6 RPM</td></tr>
                <tr><td>Max Feed Rate (F):</td><td>8818.21 mm/min (146.97 mm/s)</td></tr>
              </tbody>
            </table>
          </div>

          <p>
            This tool calibrates the relationship between <strong>mixing screw speed (RPM)</strong> and 
            material throughput (kg/hr) for the PE320 extruder. Enter your measurements, calculate 
            polynomial coefficients, and generate G-code commands for calibration tests.
          </p>

          <div className="material-info">
            <div className="input-row">
              <div className="input-field">
                <label htmlFor="material-name">Material Name:</label>
                <input
                  type="text"
                  id="material-name"
                  value={materialName}
                  onChange={(e) => setMaterialName(e.target.value)}
                  placeholder="e.g., PLA, ABS, PETG"
                />
              </div>
              <div className="input-field">
                <label htmlFor="nozzle-size">Nozzle Size (mm):</label>
                <input
                  type="number"
                  id="nozzle-size"
                  step="0.1"
                  min="0.1"
                  value={nozzleSize}
                  onChange={(e) => setNozzleSize(e.target.value)}
                  placeholder="e.g., 1.0"
                />
              </div>
            </div>
            <button className="btn-primary" onClick={autoFillSpeeds}>
              Auto-fill Test Speeds (20-100%)
            </button>
          </div>
        </section>

        {/* Instructions Panel */}
        <section className="panel">
          <div className="panel-header clickable" onClick={() => setShowInstructions(!showInstructions)}>
            <h2>Instructions</h2>
            <span className="toggle-icon">{showInstructions ? '\u25B2' : '\u25BC'}</span>
          </div>
          
          <div className="instructions-brief">
            <p>
              <strong>Quick Guide:</strong> Enter material details, use "Auto-fill Test Speeds" to set 
              mixing screw speeds, collect material samples for 60 seconds at each speed, enter weights 
              in g/min, then click Calculate.
            </p>
          </div>

          {showInstructions && (
            <div className="instructions-content">
              <h3>PE320 Calibration Process</h3>
              <p>
                This tool is specifically designed for the PE320 extruder. The calibration process 
                establishes the relationship between mixing screw speed (RPM) and material throughput (kg/hr).
              </p>

              <div className="warning-box">
                <strong>Important:</strong> This tool uses <strong>mixing screw speed</strong> (after 25:1 
                gearbox reduction), not motor speed. Maximum mixing screw speed is 159.6 RPM.
              </div>

              <h3>Equipment Needed</h3>
              <ul>
                <li>Purging bucket</li>
                <li>Precision weighing scale (0.01g accuracy)</li>
                <li>Stopwatch/timer</li>
                <li>Knife or scissors for cutting extrusion</li>
                <li>Properly dried material (moisture content {'<'}0.2%)</li>
              </ul>

              <h3>G-code Formula</h3>
              <p>For any mixing screw speed x (in RPM), the G-code command for 60-second extrusion is:</p>
              <div className="gcode-formula">
                G1 E[(x / 159.6) * 8818.21] F[(x / 159.6) * 8818.21]
              </div>
            </div>
          )}
        </section>

        {/* G-code Generator Panel */}
        <section className="panel">
          <div className="panel-header">
            <h2>G-code Generator</h2>
          </div>

          <div className="gcode-generator">
            <div className="input-row">
              <div className="input-field">
                <label htmlFor="gcode-speed">Mixing Screw Speed (RPM):</label>
                <input
                  type="number"
                  id="gcode-speed"
                  step="0.1"
                  min="0"
                  max={MAX_SCREW_SPEED}
                  value={gcodeSpeed}
                  onChange={(e) => setGcodeSpeed(e.target.value)}
                  placeholder={`0-${MAX_SCREW_SPEED}`}
                />
              </div>
              <div className="input-field">
                <label htmlFor="gcode-duration">Duration (seconds):</label>
                <input
                  type="number"
                  id="gcode-duration"
                  step="1"
                  min="1"
                  value={gcodeDuration}
                  onChange={(e) => setGcodeDuration(e.target.value)}
                />
              </div>
              <div className="input-field">
                <label>&nbsp;</label>
                <button className="btn-success" onClick={generateGcode}>
                  Generate G-code
                </button>
              </div>
            </div>

            {generatedGcode && (
              <div className="gcode-result">
                <h4>Generated G-code:</h4>
                <div className="gcode-output">{generatedGcode}</div>
                <button className="btn-secondary" onClick={copyGcode}>
                  Copy to Clipboard
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Data Table Panel */}
        <section className="panel">
          <div className="panel-header">
            <h2>Calibration Data</h2>
            <div className="header-actions">
              <button className="btn-secondary" onClick={saveData}>Save Data</button>
              <button className="btn-secondary" onClick={loadData}>Load Data</button>
              <button className="btn-primary" onClick={calculateCoefficients}>Calculate Coefficients</button>
            </div>
          </div>

          <div className="table-actions">
            <button className="btn-secondary" onClick={() => addRow()}>Add Row</button>
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Throughput (g/min)</th>
                <th>Throughput (kg/hr)</th>
                <th>Mixing Screw Speed (rpm)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}>
                  <td>{index + 1}</td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={row.gmin}
                      onChange={(e) => handleRowChange(row.id, 'gmin', e.target.value)}
                    />
                  </td>
                  <td className="kghr">{calculateKgHr(row.gmin)}</td>
                  <td>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max={MAX_SCREW_SPEED}
                      value={row.rpm}
                      onChange={(e) => handleRowChange(row.id, 'rpm', e.target.value)}
                    />
                  </td>
                  <td>
                    <button className="btn-delete" onClick={() => removeRow(row.id)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Results Panel */}
        {coefficients && (
          <section className="panel">
            <div className="panel-header">
              <h2>Calibration Coefficients</h2>
              <div className="header-actions">
                <button className="btn-secondary" onClick={exportCsv}>Export CSV</button>
              </div>
            </div>

            <p className="formula-note">
              Polynomial equation: Throughput (kg/hr) = aX3 + bX2 + cX + d, where X is mixing screw speed (RPM)
            </p>

            <table className="coefficients-table">
              <thead>
                <tr>
                  <th>X3 (a)</th>
                  <th>X2 (b)</th>
                  <th>X1 (c)</th>
                  <th>Y (d)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{coefficients.coef3}</td>
                  <td>{coefficients.coef2}</td>
                  <td>{coefficients.coef1}</td>
                  <td>{coefficients.coef0}</td>
                </tr>
              </tbody>
            </table>
          </section>
        )}

        {/* Chart Panel */}
        {coefficients && (
          <section className="panel chart-panel">
            <div className="chart-wrapper">
              <canvas ref={chartRef}></canvas>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default EStepsCalibration;
